export interface GolfAudioPlayer {
  playCardShuffle: () => void;
  playGolfClap: () => void;
  playChampagnePop: () => void;
}

function createContext(): AudioContext {
  const AudioCtor = window.AudioContext ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).webkitAudioContext;
  return new AudioCtor();
}

class GolfAudio implements GolfAudioPlayer {
  private context: AudioContext | null = null;

  private async ensureContext(): Promise<AudioContext> {
    if (!this.context) {
      this.context = createContext();
    }

    if (this.context.state === "suspended") {
      await this.context.resume();
    }

    return this.context;
  }

  private async createTone(frequency: number, duration: number, type: OscillatorType): Promise<void> {
    const ctx = await this.ensureContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.value = frequency;

    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0005, ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + duration);
  }

  private async createNoise(duration: number, frequency: number): Promise<void> {
    const ctx = await this.ensureContext();
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < data.length; i += 1) {
      const white = Math.random() * 2 - 1;
      data[i] = white * 0.4 * Math.exp(-i / (ctx.sampleRate / frequency));
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const band = ctx.createBiquadFilter();
    band.type = "highpass";
    band.frequency.value = frequency;

    const gain = ctx.createGain();
    gain.gain.value = 0.2;

    source.connect(band);
    band.connect(gain);
    gain.connect(ctx.destination);

    source.start();
    source.stop(ctx.currentTime + duration);
  }

  async playCardShuffle(): Promise<void> {
    await Promise.all([
      this.createTone(300, 0.08, "triangle"),
      this.createTone(420, 0.08, "triangle"),
      this.createTone(520, 0.08, "triangle"),
    ]);
    await this.createNoise(0.2, 450);
  }

  async playGolfClap(): Promise<void> {
    await Promise.all([
      this.createTone(1300, 0.12, "sine"),
      this.createTone(1700, 0.14, "triangle"),
    ]);
  }

  async playChampagnePop(): Promise<void> {
    await Promise.all([
      this.createTone(220, 0.2, "sawtooth"),
      this.createTone(440, 0.12, "triangle"),
      this.createNoise(0.15, 1500),
    ]);
  }
}

const audio = new GolfAudio();

export function getGolfAudio(): GolfAudioPlayer {
  return audio;
}
